import { useCallback, useEffect, useState } from "react";
import { api, fmtDate, fmtDateTime, parseApiError } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";
import { Check, ChevronLeft, ChevronRight, Clock, ImageOff, MapPin, X } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 20;

function initials(name) {
  return (name || "?").trim().split(/\s+/).slice(-2).map((w) => w[0]).join("").toUpperCase();
}

export default function DailyTaskApproval() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({ work_date: "", branch_id: "", q: "" });
  const [offset, setOffset] = useState(0);
  const [active, setActive] = useState(null);
  const [reason, setReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (filters.work_date) params.set("work_date", filters.work_date);
    if (filters.branch_id) params.set("branch_id", filters.branch_id);
    if (filters.q) params.set("q", filters.q);
    try {
      const r = await api.get(`/hrm/daily-work/pending-approval?${params.toString()}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(parseApiError(e, "Không tải được danh sách").message);
    }
  }, [filters, offset]);
  useEffect(() => { load(); }, [load]);

  const openRow = (it) => { setActive(it); setReason(""); setRejecting(false); setZoom(false); };
  const closeDrawer = () => { setActive(null); setReason(""); setRejecting(false); setZoom(false); };

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/hrm/daily-work/items/${active.id}/approve`, { version: active.version });
      toast.success("Đã duyệt");
      closeDrawer(); load();
    } catch (e) { toast.error(parseApiError(e, "Thất bại").message); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (!reason.trim()) { toast.error("Cần nêu lý do từ chối"); return; }
    setBusy(true);
    try {
      await api.post(`/hrm/daily-work/items/${active.id}/reject`, { version: active.version, rejection_reason: reason.trim() });
      toast.success("Đã từ chối");
      closeDrawer(); load();
    } catch (e) { toast.error(parseApiError(e, "Thất bại").message); }
    finally { setBusy(false); }
  };

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div data-testid="page-daily-task-approval">
      <PageHeader kicker="HRM" title="Duyệt công việc có ảnh" breadcrumb={["HRM", "Duyệt ảnh"]} />
      <div className="p-6 lg:p-8 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input type="date" value={filters.work_date} onChange={(e) => { setOffset(0); setFilters({ ...filters, work_date: e.target.value }); }} data-testid="approval-filter-date" />
          <select className="w-full h-9 border border-stone-200 rounded-sm px-2" value={filters.branch_id} onChange={(e) => { setOffset(0); setFilters({ ...filters, branch_id: e.target.value }); }} data-testid="approval-filter-branch">
            <option value="">Tất cả cơ sở</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <Input placeholder="Tìm nhân viên / công việc" value={filters.q} onChange={(e) => { setOffset(0); setFilters({ ...filters, q: e.target.value }); }} data-testid="approval-filter-q" />
        </div>

        <div className="bg-white border border-stone-200 rounded-sm divide-y">
          {items.map((it) => (
            <button key={it.id} onClick={() => openRow(it)} className="w-full text-left p-3 flex items-center gap-3 hover:bg-stone-50" data-testid={`approval-row-${it.id}`}>
              <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-900 text-xs font-bold flex items-center justify-center shrink-0">{initials(it.employee_name)}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-stone-900 truncate">{it.task_name}</div>
                <div className="text-xs text-stone-500 truncate">
                  {it.employee_name || "—"} · {it.branch_name || "Cơ sở"}{it.zone ? ` · ${it.zone}` : ""} · {it.shift_name || "Ca"}
                </div>
                <div className="text-[11px] text-stone-400 flex items-center gap-1 mt-0.5">
                  <Clock className="w-3 h-3" />{fmtDate(it.work_date)}{it.submitted_at ? ` · gửi ${fmtDateTime(it.submitted_at)}` : ""}
                </div>
              </div>
              {it.photo_url
                ? <img src={it.photo_url} alt="" className="w-12 h-12 rounded-md object-cover border border-stone-200 shrink-0" />
                : <div className="w-12 h-12 rounded-md border border-stone-200 bg-stone-50 flex items-center justify-center shrink-0"><ImageOff className="w-4 h-4 text-stone-300" /></div>}
              <ChevronRight className="w-4 h-4 text-stone-400 shrink-0" />
            </button>
          ))}
          {items.length === 0 && (
            <div className="text-center text-stone-400 py-12 text-sm" data-testid="approval-empty">Không có task nào chờ duyệt hôm nay</div>
          )}
        </div>

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-stone-500">{total} công việc · trang {page}/{pages}</span>
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-sm h-9" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} data-testid="approval-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" className="rounded-sm h-9" disabled={page >= pages} onClick={() => setOffset(offset + PAGE_SIZE)} data-testid="approval-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <Drawer open={!!active} onOpenChange={(o) => !o && closeDrawer()}>
        <DrawerContent className="max-h-[92vh]">
          <DrawerHeader className="text-left">
            <DrawerTitle className="font-serif text-xl">{active?.task_name}</DrawerTitle>
            {active?.expected_result && <p className="text-sm text-stone-600">{active.expected_result}</p>}
          </DrawerHeader>
          {active && (
            <div className="px-4 space-y-3 overflow-y-auto">
              {active.photo_url ? (
                <button type="button" onClick={() => setZoom(true)} className="block w-full" data-testid="approval-photo">
                  <img src={active.photo_url} alt="Ảnh xác minh" className="w-full max-h-72 object-contain rounded-sm border border-stone-200 bg-stone-50" />
                  <span className="text-[11px] text-stone-400">Bấm để phóng to</span>
                </button>
              ) : (
                <div className="text-sm text-stone-400 border border-dashed border-stone-200 rounded-sm py-6 text-center">Không có ảnh</div>
              )}
              <dl className="text-sm grid grid-cols-3 gap-y-1.5">
                <dt className="text-stone-500">Nhân viên</dt><dd className="col-span-2 font-medium">{active.employee_name || "—"}</dd>
                <dt className="text-stone-500">Cơ sở</dt><dd className="col-span-2">{active.branch_name || "—"}</dd>
                {active.zone && (<><dt className="text-stone-500 flex items-center gap-1"><MapPin className="w-3 h-3" />Khu vực</dt><dd className="col-span-2">{active.zone}</dd></>)}
                <dt className="text-stone-500">Ca</dt><dd className="col-span-2">{active.shift_name || "—"}</dd>
                <dt className="text-stone-500">Ngày</dt><dd className="col-span-2">{fmtDate(active.work_date)}</dd>
                {active.submitted_at && (<><dt className="text-stone-500">Gửi lúc</dt><dd className="col-span-2">{fmtDateTime(active.submitted_at)}</dd></>)}
              </dl>
              {rejecting && (
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Lý do từ chối (bắt buộc)..." className="rounded-sm" data-testid="approval-reason" />
              )}
            </div>
          )}
          <DrawerFooter className="grid grid-cols-2 gap-2">
            <Button onClick={approve} disabled={busy} className="h-12 bg-emerald-700 hover:bg-emerald-800 rounded-sm" data-testid="btn-approve">
              <Check className="w-4 h-4 mr-2" />Duyệt
            </Button>
            {rejecting ? (
              <Button onClick={reject} disabled={busy} variant="outline" className="h-12 rounded-sm border-red-300 text-red-700 hover:bg-red-50" data-testid="btn-reject-confirm">
                <X className="w-4 h-4 mr-2" />Xác nhận từ chối
              </Button>
            ) : (
              <Button onClick={() => setRejecting(true)} disabled={busy} variant="outline" className="h-12 rounded-sm border-red-300 text-red-700 hover:bg-red-50" data-testid="btn-reject">
                <X className="w-4 h-4 mr-2" />Từ chối
              </Button>
            )}
            <Button variant="outline" onClick={closeDrawer} className="rounded-sm col-span-2 h-10">Đóng</Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {zoom && active?.photo_url && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setZoom(false)} data-testid="approval-lightbox">
          <img src={active.photo_url} alt="Ảnh xác minh" className="max-w-full max-h-full object-contain" />
          <button type="button" className="absolute top-4 right-4 text-white/80" aria-label="Đóng"><X className="w-7 h-7" /></button>
        </div>
      )}
    </div>
  );
}
